import type {
  VoyageEvaluationLandingFilters,
  VoyageEvaluationSummary,
  VoyagePerformanceFilters,
  VoyagePerformanceReport,
} from "../models/voyage-evaluation.models";

const matchesText = (actual: string | null, expected: string): boolean =>
  !expected || String(actual ?? "").trim().toLowerCase() === expected.trim().toLowerCase();

const matchesDateRange = (actual: string | null, minimum: string, maximum: string): boolean => {
  if (!minimum && !maximum) return true;
  if (!actual) return false;

  const value = new Date(/^\d{4}-\d{2}-\d{2}$/.test(actual) ? `${actual}T12:00:00` : actual).getTime();
  const min = minimum ? new Date(`${minimum}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const max = maximum ? new Date(`${maximum}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;

  return !Number.isNaN(value) && value >= min && value <= max;
};

const matchesNumberRange = (actual: number | null, minimum: string, maximum: string): boolean => {
  if (!minimum && !maximum) return true;
  if (actual === null) return false;

  const min = minimum === "" ? Number.NEGATIVE_INFINITY : Number(minimum);
  const max = maximum === "" ? Number.POSITIVE_INFINITY : Number(maximum);

  return actual >= min && actual <= max;
};

const matchesBoolean = (actual: boolean | null, expected: string): boolean =>
  !expected || (expected === "yes" ? actual === true : actual === false);

export const filterVoyageEvaluationSummaries = (
  voyages: VoyageEvaluationSummary[],
  filters: VoyageEvaluationLandingFilters,
): VoyageEvaluationSummary[] => voyages.filter((voyage) =>
  matchesText(voyage.imo, filters.imo)
  && matchesText(voyage.vesselName, filters.vesselName)
  && matchesText(voyage.voyage, filters.voyage)
  && matchesText(voyage.vesselType, filters.vesselType)
  && matchesText(voyage.contractType, filters.contractType)
  && matchesText(voyage.oprType, filters.oprType)
  && matchesText(voyage.tcStatus, filters.tcStatus)
  && matchesText(voyage.voyageStatus, filters.voyageStatus)
  && matchesDateRange(voyage.completeDate, filters.completeDateFrom, filters.completeDateTo)
  && matchesBoolean(voyage.evaluated, filters.evaluated)
);

export const filterVoyagePerformanceReports = (
  reports: VoyagePerformanceReport[],
  filters: VoyagePerformanceFilters,
): VoyagePerformanceReport[] => reports.filter((report) =>
  matchesText(report.condition, filters.condition)
  && matchesBoolean(report.goodWeather, filters.goodWeather)
  && matchesBoolean(report.isZnEnriched, filters.isZnEnriched)
  && matchesNumberRange(report.steamingHours, filters.steamingHoursMin, filters.steamingHoursMax)
  && matchesNumberRange(report.currentFactor, filters.currentFactorMin, filters.currentFactorMax)
  && matchesNumberRange(report.draft, filters.draftMin, filters.draftMax)
  && matchesNumberRange(report.windBeaufort, filters.windBeaufortMin, filters.windBeaufortMax)
  && matchesNumberRange(report.noonReportWindForce, filters.noonReportWindForceMin, filters.noonReportWindForceMax)
  && matchesNumberRange(report.noonReportSwell, filters.noonReportSwellMin, filters.noonReportSwellMax)
  && matchesNumberRange(report.windSeaWaveHeight, filters.combinedWaveHeightDsMin, filters.combinedWaveHeightDsMax)
  && matchesNumberRange(report.primaryWaveHeight, filters.combinedWaveHeightMetersMin, filters.combinedWaveHeightMetersMax)
);

export const uniqueVoyageEvaluationOptions = <T,>(rows: T[], getValue: (row: T) => string | null): string[] =>
  Array.from(new Set(rows.map(getValue).filter((value): value is string => Boolean(value)))).sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true }),
  );

export const formatVoyageEvaluationDate = (value: string | null): string => {
  if (!value) return "—";

  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB");
};

export const formatVoyageEvaluationNumber = (value: number | null, digits = 1): string =>
  value === null || !Number.isFinite(value) ? "—" : value.toLocaleString(undefined, { maximumFractionDigits: digits });

export const isSafeVoyageEvaluationLink = (value: string | null): boolean => {
  if (!value) return false;

  try {
    const url = new URL(value);

    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};
